// Invites (email spec §6, §7): what the admin reads about a link
// placement. Minting invites is the sender's job through the same API; the
// admin only shows the counts and the email block.
//   GET /v1/placements/{id}/invites/summary
//   GET /v1/placements/{id}/email   (Accept: text/html for the block)

import { useQuery } from "@tanstack/react-query";
import { http } from "./http";
import { inviteKeys } from "./keys";

export interface InviteSummary {
    from: string;
    to: string;
    /** Invites created in the window. */
    invited: number;
    /** Those among them with a completed response. */
    answered: number;
}

export function useInviteSummary(placementId: string, enabled = true) {
    return useQuery({
        queryKey: inviteKeys.summary(placementId),
        queryFn: () => http.get<InviteSummary>(`/v1/placements/${placementId}/invites/summary`),
        enabled: enabled && Boolean(placementId),
    });
}

/** The email document (email spec §7): the first step, its options and
 *  the URL scheme senders build links from. */
export interface EmailDocument {
    placementId: string;
    language: string;
    survey: { name: string; title: string | null };
    step: {
        id: string;
        kind: string;
        prompt: string;
        options: { key: string; label: string | null; imageUrl: string | null; value: number | null }[];
    } | null;
    urlScheme: { invite: string; choice: string };
}

export function useEmailDocument(placementId: string, enabled = true) {
    return useQuery({
        queryKey: inviteKeys.emailDocument(placementId),
        queryFn: () => http.get<EmailDocument>(`/v1/placements/${placementId}/email`),
        enabled: enabled && Boolean(placementId),
        staleTime: 60_000,
    });
}

/** Stands where the token goes in the fetched block; the dialog swaps it
 *  for whatever the operator types, so the block is fetched once per
 *  placement rather than once per keystroke. Not a character sequence a
 *  survey's own text could contain. */
export const BLOCK_SENTINEL = "__SURVEYS_INVITE_TOKEN__";

/** The email block as HTML with the sentinel where each link's invite token
 *  goes. Fetched rather than iframed by URL so the admin's session cookie
 *  is sent the way every other call sends it. */
export function fetchEmailBlock(placementId: string): Promise<string> {
    const query = new URLSearchParams({ placeholder: BLOCK_SENTINEL }).toString();
    return http.getText(`/v1/placements/${placementId}/email?${query}`, { headers: { Accept: "text/html" } });
}

export function useEmailBlock(placementId: string, enabled: boolean) {
    return useQuery({
        queryKey: inviteKeys.emailBlock(placementId),
        queryFn: () => fetchEmailBlock(placementId),
        enabled: enabled && Boolean(placementId),
        staleTime: 60_000,
    });
}

/** The fetched block with the operator's placeholder in every link. */
export function withPlaceholder(block: string, placeholder: string): string {
    return block.split(BLOCK_SENTINEL).join(placeholder);
}
